// @flow
import { API, createDefaultHeaders } from '../../util/api'
import { del as deleteLocation } from './crud'
import { getLocations } from '../user/methods'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { removeLocation } from '../user/creators'
import { type DateObject } from '../../util/time'
import * as actionCreators from './index'
import * as locationSchema from './schemas'
import * as ScheduleActionCreators from '../../ducks/schedule'
import * as ScheduleSchema from '../../ducks/schedule/schemas'
import * as UserSchema from '../../ducks/user/schemas'
import changeCase from 'change-object-case'
import moment from 'moment'

// Types
import type { Dispatch } from '../../util/redux'
import type {
  LocationThreshold,
  SharedLocationGrant
} from '../../ducks/location/types'
import type { Role } from '../user/types'

/**
 * A module that exports auxiliary API calls related to locations
 * @module ducks/location/methods
 */

const Api = new API()

/**
 * Get schedule
 * @param {string} userAuthToken - User's authorization token
 * @param {string} locationId - Location id
 * @returns {object} Promise action related to the schedule retrieval process
 */
export function getSchedules(userAuthToken: string, locationId: string) {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(
      { locationId: { id: [locationId] } },
      { recursive: true }
    )
    const error = isInvalid(ScheduleSchema.GetSchedulesRequestSchema, reqData)
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }

    dispatch(actionCreators.locationGetSchedulesInit({ locationId }))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.locationGetSchedulesComplete(error))
      )
    }

    return Api.call('schedule/getSchedules', reqObj)
      .then(response =>
        response.json().then(json => {
          const scheduleIds = json.schedule.map(sched => sched.id)
          dispatch(ScheduleActionCreators.set(json.schedule))
          dispatch(
            actionCreators.locationGetSchedulesComplete({
              locationId,
              scheduleIds
            })
          )
        })
      )
      .catch(err => dispatch(actionCreators.locationGetSchedulesComplete(err)))
  }
}

/**
 * Get Weather
 * @param {string} userAuthToken - User auth token
 * @param {string} locationId - Location ID
 * @param {module:util/time~DateObject} startDate - Start date (defaults to now)
 * @param {module:util/time~DateObject} endDate - End date (defaults to 2 weeks from now)
 * @returns {promise} Dispatched action
 */
export function getWeather(
  userAuthToken: string,
  locationId: string,
  startDate: DateObject,
  endDate: DateObject
) {
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys(
      { locationId, startDate, endDate },
      { recursive: true }
    )
    const headers = {
      ...createDefaultHeaders(userAuthToken),
      'Content-Type': 'application/json'
    }
    const reqObj = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    dispatch(
      actionCreators.locationGetWeatherInit({ locationId, startDate, endDate })
    )

    return Api.call('location/getWeatherByLocation', reqObj)
      .then(response => response.json())
      .then(json =>
        dispatch(
          actionCreators.locationGetWeatherComplete({
            weatherForecast: json.weatherReadings,
            locationId
          })
        )
      )
      .catch(err => dispatch(actionCreators.locationGetWeatherComplete(err)))
  }
}

/**
 * Get a list of schedules that run on a given day within the time range
 * @param {string} userAuthToken - User auth token
 * @param {string} locationId - Location ID
 * @param {any} startDate - Start date; anything that momentJS can use
 * @param {any} endDate - End date; anything that momentJS can use
 * @returns {promise} Dispatched action
 */
export const getWateringDays = (
  userAuthToken: string,
  locationId: string,
  startDate: any,
  endDate: any
) => {
  return function(dispatch: Dispatch) {
    const startTime = moment(startDate).toISOString()
    const endTime = moment(endDate).toISOString()
    const reqData = changeCase.snakeKeys(
      { locationId, startTime, endTime },
      { recursive: true }
    )
    const headers = {
      ...createDefaultHeaders(userAuthToken),
      'Content-Type': 'application/json'
    }
    const reqObj = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    dispatch(
      actionCreators.locationGetWateringDaysInit({
        locationId,
        startTime,
        endTime
      })
    )

    return Api.call('location/getCalendarForTimeRange', reqObj)
      .then(response => response.json())
      .then(json =>
        dispatch(
          actionCreators.locationGetWateringDaysComplete({
            ...json,
            locationId: locationId
          })
        )
      )
      .catch(err =>
        dispatch(actionCreators.locationGetWateringDaysComplete(err))
      )
  }
}

/**
 * Get Calendar for a single day
 * @param {string} userAuthToken - User auth token
 * @param {string} locationId - Location ID
 * @param {number | string} [startDate] - Start date
 * @returns {promise} Dispatched action
 */
export const getDayCalendar = (
  userAuthToken: string,
  locationId: string,
  startDate: Object
) => {
  return function(dispatch: Dispatch) {
    // convert from 0 based to 1 based months
    const date = {
      year: Number(startDate.year),
      month: Number(startDate.month) + 1,
      day: Number(startDate.day)
    }
    const reqData = changeCase.snakeKeys(
      { locationId, date },
      { recursive: true }
    )
    const headers = {
      ...createDefaultHeaders(userAuthToken),
      'Content-Type': 'application/json'
    }
    const reqObj = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(reqData)
    }

    dispatch(actionCreators.locationGetDayCalendarInit({ locationId, date }))

    return Api.call('location/getDay', reqObj)
      .then(response => response.json())
      .then(json =>
        dispatch(
          actionCreators.locationGetDayCalendarComplete({
            ...json,
            locationId: locationId
          })
        )
      )
      .catch(err =>
        dispatch(actionCreators.locationGetDayCalendarComplete(err))
      )
  }
}

/**
 * Set a location's `favorite` flag
 * @param {string} userAuthToken - User bearer token
 * @param {string} locationId - Location ID
 * @param {boolean} favorited - Whether the location is favortied or not
 * @returns {object} Promise
 */
export function updateLocationFavorite(
  userAuthToken: string,
  locationId: string,
  favorited: boolean
) {
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys({ locationId, favorited })

    const reqObj = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        // eslint-disable-next-line
        Authorization: 'Bearer ' + userAuthToken
      }),
      body: JSON.stringify(reqData)
    }

    dispatch(actionCreators.locationUpdateFavoriteInit({ locationId }))

    return Api.call('/location/updatePermissionFavorite', reqObj)
      .then(() => {
        return dispatch(
          actionCreators.locationUpdateFavoriteComplete({
            locationId,
            favorited
          })
        )
      })
      .catch(err =>
        dispatch(actionCreators.locationUpdateFavoriteComplete(err))
      )
  }
}

/**
 * Get Weather Stations in proximity to location. Note that this DOES NOT
 * interact with app state and therefore doesn't fire any actions
 * @summary Get Weather Stations in proximity to location
 * @param {string} userAuthToken - User auth token
 * @param {string} locationId - Location Id
 * @returns {promise} - Results of api call
 */
export const getClosestWeatherStations = (
  userAuthToken: string,
  locationId: string
) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqObj = {
      method: 'GET',
      headers: createDefaultHeaders(userAuthToken)
    }

    dispatch(actionCreators.locationGetWeatherStationsInit)

    return Api.call(`location/getClosestWeatherStations/${locationId}`, reqObj)
      .then(response => response.json())
      .then(weatherStations => {
        dispatch(
          actionCreators.locationGetWeatherStationsComplete({
            locationId,
            data: { weatherStations: weatherStations.stations }
          })
        )
        return dispatch(
          actionCreators.updateMeta({
            locationId,
            data: { weatherStations: weatherStations.stations }
          })
        )
      })
  }
}

export const updateLocationWeatherStation = (
  userAuthToken: string,
  locationId: string,
  stationId: string
) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(
      { locationId, stationId },
      { recursive: true }
    )
    const reqObj = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    dispatch(
      actionCreators.locationUpdateWeatherStationInit({ locationId, stationId })
    )

    const error = isInvalid(
      locationSchema.LocationUpdateWeatherStationSchema,
      reqData
    )
    if (error) {
      return Promise.reject(
        dispatch(actionCreators.locationUpdateWeatherStationComplete(error))
      )
    }

    return Api.call('location/updateLocationWeatherStation', reqObj)
      .then(res => res.json())
      .then(json =>
        dispatch(actionCreators.locationUpdateWeatherStationComplete(json))
      )
  }
}

/**
 * Delete location and update user locationIds
 * @param {string} userAuthToken - User auth token
 * @param {string} userId - User ID
 * @param {string} locationId - Location ID
 * @returns {Map} - Results of api calls
 */
export const deleteAndRefreshUsersLocations = (
  userAuthToken: string,
  userId: string,
  locationId: string
) => {
  return function(dispatch: Dispatch) {
    const respMap = new Map()
    return dispatch(deleteLocation(userAuthToken, locationId))
      .then(respAction => respMap.set('deleteLocation', respAction))
      .then(() => dispatch(getLocations(userAuthToken, userId)))
      .then(respAction => respMap.set('getLocations', respAction))
      .then(() => respMap)
  }
}

// Sharing ////////////////////////////////////////////////////////////////////

/**
 * Share Location
 * @param  {string} userAuthToken - User auth token
 * @param  {string} locationId - Location ID
 * @param  {string} role - Role ('MANAGER' | 'QUICKRUN')
 * @param  {string} emailAddress - Email address
 * @param  {string} [expires] - Expiration date
 * @return {Promise} - `locationshareComplete` action
 */
export const share = (
  userAuthToken: string,
  locationId: string,
  role: Role,
  emailAddress: string,
  expires: ?string
) => {
  return function(dispatch: Dispatch): ?Promise<*> {
    // Make sure we have a valid role
    const roleError = isInvalid(UserSchema.RoleSchema, role)
    if (!roleError) {
      // Process share
      if (role === 'MANAGER') {
        const reqData = changeCase.snakeKeys(
          { locationId, role, emailAddress },
          { recursive: true }
        )
        const error = isInvalid(
          locationSchema.LocationShareRequestSchema,
          reqData
        )
        const reqObj = {
          method: 'POST',
          body: JSON.stringify(reqData),
          headers: new Headers({
            ...createDefaultHeaders(userAuthToken),
            'Content-Type': 'application/json'
          })
        }

        // $FlowFixMe
        dispatch(actionCreators.locationShareInit(reqData))

        if (error) {
          return Promise.reject(
            dispatch(actionCreators.locationShareComplete(error))
          )
        }

        return Api.call('location/shareLocationWithEmail', reqObj)
          .then(response => response.json())
          .then(json => dispatch(actionCreators.locationShareComplete(json)))
          .catch(err => dispatch(actionCreators.locationShareComplete(err)))
      } else if (role === 'QUICK_RUN') {
        const expiresISOString = moment(expires).toISOString()
        const reqData = changeCase.snakeKeys(
          { locationId, expires: expiresISOString, emailAddress },
          { recursive: true }
        )
        const error = isInvalid(
          locationSchema.LocationShareLimitedRequestSchema,
          reqData
        )
        const reqObj = {
          method: 'POST',
          body: JSON.stringify(reqData),
          headers: new Headers({
            ...createDefaultHeaders(userAuthToken),
            'Content-Type': 'application/json'
          })
        }

        // $FlowFixMe
        dispatch(actionCreators.locationShareInit(reqData))

        if (error) {
          return Promise.reject(
            dispatch(actionCreators.locationShareComplete(error))
          )
        }

        return Api.call('location/shareLocationQR', reqObj)
          .then(response => response.json())
          .then(json => dispatch(actionCreators.locationShareComplete(json)))
          .catch(err => dispatch(actionCreators.locationShareComplete(err)))
      }
    } else {
      // Handle bad role
      return Promise.reject(
        dispatch(actionCreators.locationShareComplete(roleError))
      )
    }
  }
}

/**
 * Revoke Share
 * @param  {string} userAuthToken - User auth token
 * @param  {Object} sharedLocationGrant - All data for the location grant to revoke
 * @param  {string} userId - Current user ID
 * @return {Promise} - `locationshareComplete` action
 */
export const revokeShare = (
  userAuthToken: string,
  sharedLocationGrant: SharedLocationGrant,
  userId: ?string // Current user ID
) => {
  return function(dispatch: Dispatch) {
    const reqObj = {
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }

    const { id, locationId, emailAddress, code } = sharedLocationGrant

    const url = code
      ? 'location/deleteSharedLocationGrant/' + code
      : 'location/deleteSharedLocation/' + id + '/' + locationId

    dispatch(
      actionCreators.locationRevokeShareInit({ locationId, emailAddress })
    )

    return Api.call(url, reqObj)
      .then(response => response.json())
      .then(json => {
        const retVal = dispatch(
          actionCreators.locationRevokeShareComplete(json)
        )
        // Cleanup
        // If this was shared with the current user, remove it from their locations
        if (json.deleted === true && userId === id) {
          dispatch(removeLocation({ userId: id, locationId }))
        } else {
          // Else remove it from the location's shares
          dispatch(actionCreators.locationRemoveShare(sharedLocationGrant))
        }
        return retVal
      })
      .catch(err => dispatch(actionCreators.locationRevokeShareComplete(err)))
  }
}

/**
 * Get location
 * @param {string} userAuthToken - User's authorization token
 * @param {string} locationId - Location id
 * @param {boolean} includeMetadata - Whether or not the response should include metadata
 * @returns {object} An action related to the location retrieval process
 */
export const viewSharedLocationGrants = (
  userAuthToken: string,
  locationId: string
) => {
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'GET',
      headers: createDefaultHeaders(userAuthToken)
    }

    dispatch(actionCreators.locationUpdateInit({ locationId }))

    return Promise.all([
      Api.call(`location/viewSharedLocations/${locationId}`, reqObj),
      Api.call(`location/viewSharedLocationGrants/true/${locationId}`, reqObj)
    ])
      .then(response => Promise.all([response[0].json(), response[1].json()]))
      .then(jsonArray => {
        const normalizedSharedLocations = jsonArray[0].sharedLocations.map(
          share => {
            return {
              ...share.user,
              locationId: share.locationId,
              role: share.role
            }
          }
        )
        const update = {
          location: {
            id: locationId
          },
          sharedLocationGrants: normalizedSharedLocations.concat(
            jsonArray[1].sharedLocationGrants
          )
        }
        dispatch(actionCreators.up(update))
        return dispatch(actionCreators.locationUpdateComplete(update))
      })
      .catch(err => dispatch(actionCreators.locationUpdateComplete(err)))
  }
}

// Limited Access /////////////////////////////////////////////////////////////

/**
 * Validate a share code
 * @param  {string} locationId - Location ID
 * @param  {string} code - Share code
 * @return {Promise<object>} - `validateShareCodeComplete` action
 */
export const validateShareCode = (locationId: string, code: string) => {
  return function(dispatch: Dispatch) {
    const reqObj = {
      headers: new Headers({
        ...createDefaultHeaders(),
        'Content-Type': 'application/json'
      })
    }

    dispatch(actionCreators.validateShareCodeInit({ locationId, code }))

    return Api.call(`location/validateQRCode/${locationId}/${code}/`, reqObj)
      .then(response => response.json())
      .then(json => dispatch(actionCreators.validateShareCodeComplete(json)))
      .catch(err => dispatch(actionCreators.validateShareCodeComplete(err)))
  }
}

// Thresholds /////////////////////////////////////////////////////////////////

export const updateThreshold = (
  userAuthToken: string,
  locationId: string,
  locationThreshold: LocationThreshold
) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(
      { locationId, locationThreshold },
      { recursive: true }
    )
    const reqObj = {
      method: 'POST',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }
    const error = isInvalid(
      locationSchema.UpdateLocationThresholdRequestSchema,
      reqData
    )

    dispatch(actionCreators.locationUpdateInit({ locationId }))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.locationUpdateComplete(error))
      )
    } else {
      return Api.call('location/updateLocationThreshold', reqObj)
        .then(resp => resp.json())
        .then(resp => {
          dispatch(actionCreators.up(resp))
          return dispatch(actionCreators.locationUpdateComplete(resp))
        })
        .catch(err => dispatch(actionCreators.locationUpdateComplete(err)))
    }
  }
}
